import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  ClipboardList, 
  Users, 
  Calendar, 
  FileText, 
  BarChart3, 
  Shield, 
  Car, 
  Package,
  DollarSign,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Globe,
  Lock,
  Zap,
  HeartPulse,
  GraduationCap,
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Users,
      title: "Employee Management",
      description: "Track employees, certifications, time entries, and payroll all in one place.",
      color: "text-cyan-400",
    },
    {
      icon: FileText,
      title: "Quotes & Invoices",
      description: "Create professional quotes, convert to invoices, and track payments seamlessly.",
      color: "text-orange-400",
    },
    {
      icon: Calendar,
      title: "Smart Scheduling",
      description: "Schedule jobs, manage recurring appointments, and coordinate your team efficiently.",
      color: "text-cyan-400",
    },
    {
      icon: DollarSign,
      title: "Financial Tracking",
      description: "Monitor accounts receivable, accounts payable, and generate financial reports.",
      color: "text-orange-400",
    },
    {
      icon: Car,
      title: "Fleet Management",
      description: "Track vehicles, maintenance schedules, fuel costs, and driver assignments.",
      color: "text-cyan-400",
    },
    {
      icon: Package,
      title: "Equipment Tracking",
      description: "Manage equipment inventory, assignments, and maintenance records.",
      color: "text-orange-400",
    },
    {
      icon: HeartPulse,
      title: "Health & Safety",
      description: "Maintain safety documents, track acknowledgments, and ensure compliance.",
      color: "text-cyan-400",
    },
    {
      icon: GraduationCap,
      title: "Training Programs",
      description: "Create training materials, assign to employees or clients, track completion.",
      color: "text-orange-400",
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description: "Gain insights with comprehensive reports and business analytics.",
      color: "text-cyan-400",
    },
  ];

  const benefits = [
    "Multi-country payroll with tax calculations",
    "Client & employee self-service portals",
    "Stripe integration for online payments",
    "Document templates with live preview",
    "Compliance alerts and reminders",
    "Role-based access control",
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-[#0a1628]/90 backdrop-blur-xl border-b border-cyan-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img
                src="/business-genie-logo.png"
                alt="Business Genie"
                className="w-10 h-10 rounded-xl object-contain bg-white/5"
                data-testid="landing-bg-logo"
              />
              <span className="text-xl font-bold text-white">Business Genie</span>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                className="text-gray-300 hover:text-white hover:bg-white/10"
                onClick={() => navigate("/login")}
              >
                Sign In
              </Button>
              <Button 
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg shadow-cyan-500/25"
                onClick={() => navigate("/register")}
                data-testid="get-started-btn"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-8">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-400">Comprehensive Business Management</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Manage Your Business<br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-400 bg-clip-text text-transparent">
              Like Magic
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto mb-10">
            Business Genie is your all-in-one platform for employee management, invoicing, 
            scheduling, fleet tracking, and comprehensive business analytics.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg px-8 py-6 shadow-lg shadow-cyan-500/25"
              onClick={() => navigate("/register")}
              data-testid="hero-cta-btn"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-white/10 hover:text-white text-lg px-8 py-6"
              onClick={() => navigate("/login")}
            >
              View Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0d1d32]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need to Run Your Business
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Comprehensive tools designed for service businesses, contractors, and property management companies.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-[#0a1628] rounded-xl p-6 border border-gray-800 hover:border-cyan-500/50 transition-all duration-300 group"
                data-testid={`feature-${index}`}
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.color === 'text-cyan-400' ? 'from-cyan-500/20 to-blue-500/20' : 'from-orange-500/20 to-amber-500/20'} flex items-center justify-center mb-4`}>
                  <feature.icon className={`w-6 h-6 ${feature.color}`} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Built for Modern Businesses
              </h2>
              <p className="text-gray-400 mb-8">
                Business Genie combines powerful features with an intuitive interface, 
                helping you save time and grow your business.
              </p>
              
              <div className="grid gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                    </div>
                    <span className="text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-6 border border-cyan-500/20">
                <Globe className="w-8 h-8 text-cyan-400 mb-3" />
                <h4 className="text-white font-semibold mb-1">Multi-Country Support</h4>
                <p className="text-gray-400 text-sm">Canada, USA, UK payroll & tax</p>
              </div>
              <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 rounded-xl p-6 border border-orange-500/20">
                <Lock className="w-8 h-8 text-orange-400 mb-3" />
                <h4 className="text-white font-semibold mb-1">Secure & Compliant</h4>
                <p className="text-gray-400 text-sm">Audit logs & access control</p>
              </div>
              <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 rounded-xl p-6 border border-orange-500/20">
                <Zap className="w-8 h-8 text-orange-400 mb-3" />
                <h4 className="text-white font-semibold mb-1">Fast & Efficient</h4>
                <p className="text-gray-400 text-sm">Streamlined workflows</p>
              </div>
              <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-6 border border-cyan-500/20">
                <Shield className="w-8 h-8 text-cyan-400 mb-3" />
                <h4 className="text-white font-semibold mb-1">Terms & Compliance</h4>
                <p className="text-gray-400 text-sm">Legal document management</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cyan-900/30 to-blue-900/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-gray-400 mb-8 text-lg">
            Join thousands of businesses using Business Genie to streamline operations and boost productivity.
          </p>
          <Button 
            size="lg"
            className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-lg px-10 py-6 shadow-lg shadow-orange-500/25"
            onClick={() => navigate("/register")}
            data-testid="cta-btn"
          >
            Get Started for Free
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img
                src="/business-genie-logo.png"
                alt="Business Genie"
                className="w-8 h-8 rounded-lg object-contain bg-white/5"
                data-testid="landing-footer-logo"
              />
              <span className="text-lg font-semibold text-white">Business Genie</span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <a href="/legal/terms" className="hover:text-cyan-400 transition-colors" data-testid="landing-terms-link">Terms of Service</a>
              <a href="/legal/privacy" className="hover:text-cyan-400 transition-colors" data-testid="landing-privacy-link">Privacy Policy</a>
              <a href="/login" className="hover:text-cyan-400 transition-colors">Sign In</a>
            </div>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Business Genie. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
